/*
 * dgm-facets-ui.js — koppelt de facet-engine aan het thema.
 *
 * Ontwerpregel die alles bepaalt: de HTML die de server stuurt is een volledig werkende,
 * gepagineerde, ongefilterde collectiepagina. Deze laag is puur additief en mag niets
 * kapotmaken dat er al staat. Elke faalconditie leidt tot "doe niets", niet tot een lege
 * pagina.
 *
 * Waarom een eigen laag en niet Eurus' xFiltersAndSortby: dat component leunt op
 * Shopify's filter-engine, en die filtert niet boven 5.000 producten (gemeten: zeven van
 * zeven filterparams laten het aantal onveranderd). Het formulier zou submitten naar een
 * engine die de parameters negeert.
 *
 * Kaarten komen uit het THEMA, niet uit JS: /search?q=<handles>&view=dgm-cards leest de
 * handles via search.terms en rendert card-product.liquid. Zie
 * theme/templates/search.dgm-cards.liquid. Daarom staat hier geen enkele kaart-markup —
 * dat is de hele reden dat deze aanpak onderhoudbaar blijft.
 */

/* De engine wordt DYNAMISCH geïmporteerd, via de URL uit de config.
 *
 * Hier stond `import { FacetIndex, query } from './dgm-facet-engine.js'`. Zo'n relatief pad
 * heeft geen versietoken, en Shopify serveert voor zo'n URL een ouder build-artefact. Gemeten
 * op 21 augustus 2026: de Admin API meldde het themabestand als bijgewerkt, maar de browser
 * kreeg de engine van vóór de tweewaardige voorraadfilter. Zichtbaar gevolg: een
 * "Op voorraad"-vinkje dat niet uit te zetten was en geen "Niet op voorraad"-optie.
 *
 * `asset_url` in dgm-facets-config.liquid zet het versietoken erop, dus de URL volgt het
 * bestand. De fallback op het relatieve pad staat er voor het geval een oude, gecachede config
 * nog geen `engine`-sleutel heeft: dan werkt het als voorheen in plaats van helemaal niet. */
let FacetIndex = null;
let query = null;

async function laadEngine(cfg) {
  const url = (cfg.assets && cfg.assets.engine) || './dgm-facet-engine.js';
  const mod = await import(url);
  FacetIndex = mod.FacetIndex;
  query = mod.query;
  log('engine geladen', url);
}

const CONFIG_ID = 'dgm-facets-config';
const CARDS_VIEW = 'dgm-cards';
const FETCH_TIMEOUT_MS = 2500;
const CARDS_TIMEOUT_MS = 8000;   // 20 kaarten is ~60 KB; gemeten 428 ms, ruim marge
const BREAKER_KEY = 'dgm:facets:off';
const BREAKER_MINUTES = 10;

/* De breaker was een ontwerpfout in twee opzichten, en die hebben me een ronde gekost:
 *
 *  - hij was GLOBAAL, dus een mislukte laadpoging op /collections/all zette ook
 *    /collections/supplementen tien minuten stil, in dezelfde tab, zonder zichtbaar spoor;
 *  - hij sloeg toe bij de EERSTE fout, dus één transiënte 404 tijdens een deploy
 *    onderdrukte de kolom terwijl er niets meer aan de hand was.
 *
 * Nu per asset-set, pas na twee mislukkingen, en met een ontsnapping: ?dgm-reset=1 wist
 * hem, ?dgm-debug=1 laat de staat zien. Een stille onderdrukking zonder uitweg is erger
 * dan een zichtbare fout.
 */
function breakerKeyFor(cfg) {
  return `${BREAKER_KEY}:${(cfg && cfg.assetPrefix) || 'default'}`;
}
const MAX_HANDLES_PER_REQUEST = 20;  // harde Liquid-limiet van all_products

/* --- kleine helpers ------------------------------------------------------- */

const log = (...a) => { if (debugAan()) console.log('[dgm-facets]', ...a); };
const warn = (...a) => console.warn('[dgm-facets]', ...a);

/** Debugmodus: expliciet via ?dgm-debug=1, of automatisch op een thema-preview. Zo is de
 *  staat zichtbaar terwijl je aan het inrichten bent, en stil voor echte bezoekers. */
function debugAan() {
  if (window.DGM_FACETS_DEBUG) return true;
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.has('dgm-debug') || sp.has('preview_theme_id');
  } catch (e) { return false; }
}

/* Waarom dit bestaat: toen de kolom leeg bleef was er geen enkel spoor om op af te gaan —
 * geen melding op de pagina, alleen een console-warning die niemand ziet. Dat kostte een
 * hele ronde aan gissen. Een laag die stil kan falen hoort te vertellen dát hij faalt.
 *
 * De melding komt ALLEEN in beeld in debugmodus. Voor een echte bezoeker blijft het
 * fail-open-gedrag ongewijzigd: geen kolom, wel een werkende pagina. */
function status(code, bericht) {
  const st = { code, bericht, tijd: new Date().toISOString() };
  window.dgmFacetsStatus = st;
  if (code === 'ok') { log(bericht); return st; }
  warn(`${code}: ${bericht}`);
  if (!debugAan()) return st;
  const doel = document.querySelector('[data-dgm-list]');
  if (doel && !doel.querySelector('[data-dgm-status]')) {
    const p = document.createElement('p');
    p.setAttribute('data-dgm-status', code);
    p.style.cssText = 'padding:.75rem;border:1px dashed rgba(150,0,0,.5);font-size:.85rem;line-height:1.4';
    p.textContent = `Filters niet geladen (${code}). ${bericht}`;
    doel.appendChild(p);
  }
  return st;
}

function readConfig() {
  const el = document.getElementById(CONFIG_ID);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    warn('config is geen geldige JSON', e);
    return null;
  }
}

/** Circuit breaker: na twee mislukkingen in een sessie tien minuten niets proberen.
 *  Voorkomt dat elke pageview op een trage of stille index wacht. */
function breakerTripped(cfg) {
  try {
    if (new URLSearchParams(window.location.search).has('dgm-reset')) {
      for (const k of Object.keys(sessionStorage)) {
        if (k.startsWith(BREAKER_KEY)) sessionStorage.removeItem(k);
      }
      warn('breaker gewist via ?dgm-reset=1');
      return false;
    }
    const raw = sessionStorage.getItem(breakerKeyFor(cfg));
    if (!raw) return false;
    const [until, fouten] = raw.split('|').map(Number);
    if (fouten < 2) return false;                      // pas na twee mislukkingen
    if (Number.isFinite(until) && Date.now() < until) return true;
    sessionStorage.removeItem(breakerKeyFor(cfg));
  } catch (e) { /* sessionStorage kan geblokkeerd zijn; dan geen breaker */ }
  return false;
}

function tripBreaker(cfg) {
  try {
    const key = breakerKeyFor(cfg);
    const prev = Number((sessionStorage.getItem(key) || '0|0').split('|')[1]) || 0;
    sessionStorage.setItem(key, `${Date.now() + BREAKER_MINUTES * 60000}|${prev + 1}`);
  } catch (e) { /* niets */ }
}

async function fetchJSON(url, timeoutMs) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: ctrl.signal, credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status} op ${url}`);
    return await res.json();
  } finally {
    clearTimeout(t);
  }
}

/* --- URL-state ------------------------------------------------------------
 * Exact Shopify's eigen parameters. Dat is geen cosmetiek: het houdt bestaande
 * deeplinks, Ads-landingspagina's en e-mailcampagnes geldig, en het geeft één
 * URL-grammatica voor alle 751 collecties — de kleine collecties gebruiken deze params
 * native.
 * ---------------------------------------------------------------------- */

/* Staat het voorraadfilter standaard aan? Gevuld uit de config in
 * snippets/dgm-facets-config.liquid, zodat dit één regel in het thema is en geen
 * indexrebuild vraagt. Valt terug op true: dat is de gevraagde standaard. */
let DEFAULT_IN_STOCK = true;

const PARAM_TO_KEY = new Map();   // 'filter.p.vendor' -> 'vendor'
const KEY_TO_PARAM = new Map();

function registerParams(facetDefs) {
  for (const f of facetDefs) {
    if (!f.param || !f.key) continue;
    PARAM_TO_KEY.set(f.param, f.key);
    KEY_TO_PARAM.set(f.key, f.param);
  }
}

/* Facetten die GEEN lijst met waarden zijn en dus hun eigen behandeling hebben.
 *
 * `availability` staat als facet in config/facets.json en komt daardoor in KEY_TO_PARAM
 * terecht. De lussen hieronder gaan uit van een array. Zolang de waarde `['1']` was ging dat
 * goed; toen die op 2026-08-18 een boolean werd, gooide `for (const v of true)` een
 * TypeError in urlFromState.
 *
 * Gevolg was precies wat op /collections/supplementen te zien was: de filterkolom rendeert
 * nog, want die komt vóór de fout, maar de grid werd nooit vervangen. Je zag dus de native,
 * ONGEFILTERDE grid van 9.057 producten met uitverkochte artikelen erin, terwijl het
 * voorraadfilter in de kolom aan stond. En omdat urlFromState de fout gooide, kregen de
 * paginalinks geen adres en deed bladeren niets. */
const EIGEN_BEHANDELING = new Set(['availability']);

function filtersFromURL(search = window.location.search) {
  const sp = new URLSearchParams(search);
  const filters = {};
  for (const [param, key] of PARAM_TO_KEY) {
    if (EIGEN_BEHANDELING.has(key)) continue;
    const vals = sp.getAll(param);
    if (vals.length) filters[key] = vals;
  }
  const gte = sp.get('filter.v.price.gte');
  const lte = sp.get('filter.v.price.lte');
  // De URL is in euro's (zoals Shopify die schrijft), de index in centen.
  if (gte != null && gte !== '') filters.price_gte = Math.round(parseFloat(gte) * 100);
  if (lte != null && lte !== '') filters.price_lte = Math.round(parseFloat(lte) * 100);
  /* Voorraad is drietrapsstand, niet twee.
   *
   *   filter.v.availability=1   -> alleen op voorraad (Shopify's eigen param, dus de
   *                                native fallback en bestaande deeplinks blijven werken)
   *   dgm.voorraad=alles        -> de bezoeker wil UITDRUKKELIJK ook uitverkocht zien
   *   geen van beide            -> de standaard uit de config; die staat op "op voorraad"
   *
   * Waarom een eigen param voor "alles" en niet Shopify's `filter.v.availability=0`: die
   * betekent native "alleen UITVERKOCHT". Zou de engine uitvallen, dan kreeg de bezoeker
   * precies de omgekeerde collectie te zien. Een param die Shopify niet kent wordt in de
   * fallback genegeerd, en dan ziet hij alles — de veilige kant. */
  /* Voorraad kent drie standen en gebruikt Shopify's EIGEN parameter, die meerdere keren
   * mag voorkomen:
   *   filter.v.availability=1            alleen op voorraad
   *   filter.v.availability=0            alleen uitverkocht
   *   beide                              geen beperking
   *   geen parameter                     de standaard uit de config (op voorraad)
   *   dgm.voorraad=alles                 de bezoeker wil uitdrukkelijk alles zien
   *
   * Die laatste marker blijft nodig: "niets aangevinkt" kan een URL niet uitdrukken, en
   * zonder marker zou de standaard bij de volgende klik terugkeren. Hij wordt ook nog
   * gelezen voor links en sessies van vóór deze wijziging. */
  const av = sp.getAll('filter.v.availability').filter((v) => v === '1' || v === '0');
  if (av.length) {
    filters.availability = av;
  } else if (sp.get('dgm.voorraad') === 'alles') {
    filters.availability = [];
  } else {
    filters.availability = DEFAULT_IN_STOCK ? ['1'] : [];
  }
  /* Vrije zoekterm. Eigen parameter `dgm.zoek` en NIET Shopify's `q`: die stuurt een
   * bezoeker naar de zoekpagina en zou bij een engine-uitval de collectie verlaten. Een
   * parameter die Shopify niet kent wordt in de native terugval genegeerd, en dan ziet de
   * bezoeker de ongefilterde collectie — de veilige kant, net als bij `dgm.voorraad`. */
  const zoek = (sp.get('dgm.zoek') || '').trim();
  if (zoek) filters.zoek = zoek;

  return {
    filters,
    sortBy: sp.get('sort_by') || 'manual',
    page: Math.max(1, parseInt(sp.get('page') || '1', 10) || 1),
  };
}

function urlFromState(state) {
  const sp = new URLSearchParams();
  for (const [key, param] of KEY_TO_PARAM) {
    if (EIGEN_BEHANDELING.has(key)) continue;
    for (const v of state.filters[key] || []) sp.append(param, v);
  }
  if (state.filters.price_gte != null) sp.set('filter.v.price.gte', String(state.filters.price_gte / 100));
  if (state.filters.price_lte != null) sp.set('filter.v.price.lte', String(state.filters.price_lte / 100));
  const av = Array.isArray(state.filters.availability)
    ? state.filters.availability.filter((v) => v === '1' || v === '0')
    : (state.filters.availability === true ? ['1'] : []);
  if (av.length) {
    for (const v of av) sp.append('filter.v.availability', v);
  } else if (DEFAULT_IN_STOCK) {
    // Niets aangevinkt terwijl de standaard aan staat: dat moet vastgehouden worden, anders
    // springt het filter bij de volgende klik weer aan.
    sp.set('dgm.voorraad', 'alles');
  }
  if (state.filters.zoek) sp.set('dgm.zoek', state.filters.zoek);
  if (state.sortBy && state.sortBy !== 'manual') sp.set('sort_by', state.sortBy);
  if (state.page > 1) sp.set('page', String(state.page));
  const qs = sp.toString();
  return window.location.pathname + (qs ? '?' + qs : '');
}

/* --- kaarten ophalen ------------------------------------------------------ */

/** Haal de kaarten voor een lijst handles op bij het thema zelf.
 *  Splitst in chunks van 20 omdat `all_products` in Liquid daar hard stopt, en vuurt die
 *  parallel af zodat een grid van 40 net zo snel is als één van 20. */
async function fetchCards(handles) {
  const chunks = [];
  for (let i = 0; i < handles.length; i += MAX_HANDLES_PER_REQUEST) {
    chunks.push(handles.slice(i, i + MAX_HANDLES_PER_REQUEST));
  }
  const parts = await Promise.all(chunks.map(async (chunk) => {
    const q = encodeURIComponent(chunk.join(' '));
    const url = `${window.Shopify?.routes?.root || '/'}search?q=${q}&view=${CARDS_VIEW}`;
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), CARDS_TIMEOUT_MS);
    try {
      const res = await fetch(url, { signal: ctrl.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status} bij kaarten ophalen`);
      return await res.text();
    } finally {
      clearTimeout(t);
    }
  }));

  const html = parts.join('');

  // Het template geeft een requested/rendered-staart mee. Een stil verschil daartussen is
  // precies hoe een grid onopgemerkt producten weglaat, dus dat controleren we expliciet.
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  let requested = 0;
  let rendered = 0;
  for (const meta of doc.querySelectorAll('#dgm-cards-meta')) {
    try {
      const m = JSON.parse(meta.textContent);
      requested += m.requested || 0;
      rendered += m.rendered || 0;
    } catch (e) { /* negeren */ }
  }
  if (requested !== rendered) {
    warn(`kaarten: ${requested} gevraagd, ${rendered} gerenderd — verschil van ${requested - rendered}`);
  }
  return { html, requested, rendered };
}

/* --- opstarten ------------------------------------------------------------ */

export async function boot() {
  const cfg = readConfig();
  if (!cfg) { log('geen config op deze pagina'); return null; }
  if (cfg.engine !== 'custom') { log('engine=native, niets te doen'); return null; }
  log('config gelezen', cfg.assetPrefix, cfg.collection.handle);
  if (breakerTripped(cfg)) {
    status('breaker-actief', 'circuit breaker actief na twee mislukte pogingen — voeg'
      + ' ?dgm-reset=1 aan de URL toe om het opnieuw te proberen');
    return null;
  }

  let index;
  let facetConfig;
  try {
    // Manifest eerst: die is klein en vertelt of de index bruikbaar is voordat we
    // 1,6 MB gaan ophalen.
    const manifest = await fetchJSON(cfg.assets.manifest, FETCH_TIMEOUT_MS);
    const ageHours = (Date.now() - Date.parse(manifest.builtAt)) / 36e5;
    /* Twee grenzen, niet één. De oude enkele grens van 12 uur zette de filterkolom uit
     * zodra de indexbuild één nacht had overgeslagen: op 2026-07-30 om 07:13 UTC was de
     * index 12,1 uur oud en zag de bezoeker alleen nog de kop "Filter". De index bepaalt
     * uitsluitend welke producten in een resultaat zitten — prijs, voorraad en de kaart
     * komen live uit Shopify — dus een paar dagen scheefloop is véél minder schadelijk dan
     * geen filters. Zie de toelichting in snippets/dgm-facets-config.liquid. */
    const maxAge = cfg.limits?.maxIndexAgeHours ?? 72;
    const warnAge = cfg.limits?.warnIndexAgeHours ?? 24;
    if (!(ageHours < maxAge)) {
      warn(`index is ${ageHours.toFixed(1)}u oud (max ${maxAge}u) — native pagina blijft staan`);
      return null;
    }
    if (ageHours >= warnAge) {
      warn(`index is ${ageHours.toFixed(1)}u oud (waarschuwing boven ${warnAge}u) — de`
        + ' filters werken, maar de indexbuild staat waarschijnlijk stil.'
        + ' Controleer nl.gezondheidsmarkt.facetindex.');
    }

    await laadEngine(cfg);
    const [dict, products, collections, fcfg] = await Promise.all([
      fetchJSON(cfg.assets.dict, CARDS_TIMEOUT_MS),
      fetchJSON(cfg.assets.products, CARDS_TIMEOUT_MS),
      fetchJSON(cfg.assets.collections, CARDS_TIMEOUT_MS),
      fetchJSON(cfg.assets.facetConfig || cfg.assets.dict, FETCH_TIMEOUT_MS).catch(() => null),
    ]);
    index = new FacetIndex({ dict, products, collections, manifest });
    facetConfig = cfg.facetConfig || fcfg || { facets: [], hide_rules: {} };
  } catch (e) {
    status('index-niet-geladen', `${e.message}. Controleer of de assets op dit thema staan`
      + ` met prefix '${cfg.assetPrefix}'.`);
    tripBreaker(cfg);
    return null;
  }

  registerParams(facetConfig.facets || []);

  /* Standaard voor het voorraadfilter uit de config overnemen. Expliciet `=== false`
   * vergelijken en niet op waarheid testen: een ontbrekende sleutel moet de standaard
   * (aan) houden, niet stil uitzetten. */
  if (cfg.defaults && cfg.defaults.inStockOnly === false) DEFAULT_IN_STOCK = false;
  const actieve = (facetConfig.facets || []).filter((f) => f.status !== 'uitgesteld').length;
  if (!actieve) {
    status('geen-facetten-geconfigureerd',
      'de facetconfiguratie bevat geen actieve facetten — controleer config/facets.json.');
    return null;
  }
  status('ok', `index geladen: ${index.n} producten, ${index.ageHours.toFixed(1)}u oud,`
    + ` ${actieve} actieve facetten`);

  const api = {
    index,
    facetConfig,
    collection: cfg.collection.handle,
    // Het ongefilterde totaal zoals de SERVER het telt. Nodig voor de regel "X van Y
    // producten"; de engine geeft alleen het gefilterde totaal terug, en het indexcijfer
    // wijkt een paar producten af van dat van de server (9.059 tegen 9.057).
    collectionTotal: (cfg.collection && cfg.collection.productsTotal) || null,
    edges: cfg.edges,          // settings.edges_type
    /* De afronding van filter-elementen (chips, duim van de prijsschuif) komt bij Eurus
     * NIET uit edges_type maar uit filter_edge_style + buttons_type — zie de toelichting in
     * dgm-facets-config.liquid. Terugvallen op edges is puur voor een oude cache: een
     * bezoeker die de nieuwe JS al heeft maar de config-JSON nog uit de HTML-cache haalt. */
    filterEdges: cfg.filterEdges || cfg.edges,
    money: cfg.money || { symbol: '€', decimal: ',', thousands: '.' },
    strings: cfg.strings || {},
    textSizes: cfg.textSizes || {},
    // Standaard dicht. Expliciet `=== false` zodat een ontbrekende sleutel niet stil
    // terugvalt op het oude gedrag.
    groupsStartCollapsed: !(cfg.defaults && cfg.defaults.groupsStartCollapsed === false),
    /** Voer een query uit zonder de DOM aan te raken. Handig om te testen en te meten. */
    run(state) {
      const s = state || filtersFromURL();
      return query(index, this.collection, s.filters, {
        sortBy: s.sortBy,
        page: s.page,
        limit: cfg.limits?.pageSize ?? 20,
        facetConfig,
      });
    },
    filtersFromURL,
    urlFromState,
    fetchCards,
  };

  // Beschikbaar maken voor de rest van de theme-code en voor handmatig meten in de
  // console. Bewust geen automatische DOM-overname hier: die stap komt in de facet-UI
  // zelf, zodat deze module in isolatie te testen blijft.
  window.dgmFacets = api;
  document.dispatchEvent(new CustomEvent('dgm:facets:ready', { detail: api }));

  // Vangnet tegen de klasse fout waar ik nu tegenaan liep: als niemand het event oppakt
  // (render-module niet geladen, of een fout tijdens mount), blijft de kolom leeg zonder
  // uitleg. Na een tick controleren of er daadwerkelijk gerenderd is.
  setTimeout(() => {
    const lijst = document.querySelector('[data-dgm-list]');
    if (lijst && lijst.innerHTML.trim() === '') {
      status('niet-gerenderd',
        'de index is geladen maar de kolom is niet gevuld — dgm-facets-render.js ontbreekt'
        + ' of gaf een fout. Kijk in de console.');
    }
  }, 500);

  return api;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { boot(); });
} else {
  boot();
}

// registerParams is ook geëxporteerd om te kunnen testen. Zonder registratie is
// KEY_TO_PARAM leeg en loopt de lus in urlFromState niet, waardoor een test over die lus
// vals groen staat — precies wat er op 2026-08-20 gebeurde.
export { filtersFromURL, urlFromState, fetchCards, readConfig, registerParams };
